import { toTwoDecimalString } from "@/modules/agent/utils";
import { Input } from "@nextui-org/react";
import { ChangeEvent, FC, useEffect, useState } from "react";

type ZFloatNumberFieldProps = {
  value: string | number;
  onChange: (value: string) => void;
  title?: string;
  placeholder?: string;
  isDisabled?: boolean;
  min?: number;
  max?: number;
  showTitle?: boolean;
};

const ZFloatNumberField: FC<ZFloatNumberFieldProps> = ({
  value,
  onChange,
  title,
  placeholder,
  isDisabled,
  min,
  max,
  showTitle = true,
}) => {
  const [inputValue, setInputValue] = useState(String(toTwoDecimalString(`${value}`) ?? ""));

  useEffect(() => {
    const valueStr = Number(value ?? "");
    if (Number(inputValue) != valueStr) {
      setInputValue(String(valueStr));
    }
  }, [value, inputValue]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value;

    // Normalize leading zeros
    if (/^0\d+/.test(raw)) {
      raw = raw.replace(/^0+/, "");
    } else if (/^0\d+\./.test(raw)) {
      raw = raw.replace(/^0+/, "0");
    }

    if (raw === "") {
      setInputValue("");
      onChange("");
      return;
    }

    if (!/^(\d+)?(\.\d{0,2})?$/.test(raw)) {
      return;
    }

    const num = parseFloat(raw);

    // Clamp the value between min and max, if defined
    if (!isNaN(num)) {
      if (min !== undefined && num < min) {
        raw = String(min);
      } else if (max !== undefined && num > max) {
        raw = String(max);
      }
    }

    setInputValue(raw);
    onChange(raw);
  };

  return (
    <div className="relative">
      {title && showTitle && (
        <div
          className="bg-[#F9F7F7] text-[#717171] font-medium py-2 px-4 text-[10px] leading-4"
          style={{
            border: "1px solid #E4E4E7",
            borderLeft: "0px",
            borderRight: "0px",
          }}
        >
          {title}
        </div>
      )}
      <Input
        className="text-[#121212] font-light bg-white focus-within:text-[#000000]"
        classNames={{
          base: "shadow-none border-none rounded-none truncate",
          inputWrapper: `border-[1px] rounded-md`,
          input: "!text-black",
        }}
        variant="bordered"
        labelPlacement="outside"
        value={inputValue}
        type="text"
        inputMode="decimal"
        placeholder={placeholder}
        onChange={handleInputChange}
        disabled={isDisabled}
      />
    </div>
  );
};

export default ZFloatNumberField;
